import { isPlatformBrowser } from '@angular/common';
import {computed, Directive, effect, ElementRef, inject, input, PLATFORM_ID, Renderer2, signal} from '@angular/core';
import {ThemeService} from '../services/theme.service';

/**
 * A directive that apply a lazy blur for images (LQIP)
 *
 * @example
 * <img src="http://domain.co/image.webp" imgLazyBlur />
 *
 * <b>Important note:</b> This is not a replacement to NgOptimizedImage. This is a lighter version
 * that do not force a lot of the rules that NgOptimizedImage does. Thus its easier to integrate
 * with existing code built without optimization in mind. But its better to use the NgOptimizedImage directive
 * for future components and replace this directive with the NgOptimizedImage when possible
 */
@Directive({
  selector: 'img[imgLazyBlur]',
  standalone: true,
  host: {
    '[src]': 'currentSrc()',
    '[style.filter]': 'isLoading() ? "blur(20px)" : "none"',
    '[style.transition]': '"filter 0.4s ease-in-out"',
    '[style.overflow]': '"hidden"'
  }
})
export class ImgLazyBlurDirective {
  private platformId = inject(PLATFORM_ID)
  private themeService = inject(ThemeService);
  private placeholder = computed(() =>
    this.themeService.darkMode()
      ? 'assets/images/placeholders/lqip-dark.webp'
      : 'assets/images/placeholders/lqip.webp'
  );
  realSrc = input.required<string>({ alias: 'src' })
  currentSrc = signal<string>(this.placeholder())
  isLoading = signal<boolean>(true)

  constructor() {
    effect(() => {
      const targetUrl = this.realSrc();

      if (!targetUrl || !targetUrl.trim()) {
        this.currentSrc.set(this.placeholder());
        this.isLoading.set(false);
        return;
      }

      this.currentSrc.set(this.placeholder());
      this.isLoading.set(true)

      if (isPlatformBrowser(this.platformId)) {
        const backgroundLoader = new Image();
        backgroundLoader.src = targetUrl;

        backgroundLoader.onload = () => {
          this.currentSrc.set(targetUrl)
          this.isLoading.set(false)
        }

        backgroundLoader.onerror = () => {
          console.error(`[LQIP Directive] Failed to load image at: ${targetUrl}`)
          this.isLoading.set(false)
        }
      } else {
        this.currentSrc.set(this.placeholder())
      }
    });
  }
}
